import { NoteDetailsTable } from 'components/core-v2/organisms/tables/note-details'
import { NoteDetailsTableStubbed } from 'components/core-v2/organisms/tables/note-details/stubbed'
import { Loading } from 'components/core-v2/pages'
import { isThisMonth, subMonths } from 'date-fns'
import { intersection } from 'lodash'
import { useParams } from 'react-router-dom'
import { useUser } from 'utilities/hooks/useUser'

import {
  ApiNote,
  DOWNLOAD_API,
  useGetByTenantAccountQuery,
  useGetByTenantDocumentationNoteAndNoteIdSummaryQuery
} from '@/api'
import { Tabs, TopUpWithdrawNoteForm } from '@/components/core-v2'
import { PATH, TENANT } from '@/const'
import { getLastDayOfEveryMonthBetweenDates } from '@/utilities/date'

import { getUserDisplayInfo, isCollateralUser } from '../../../tradebuilder-v2/util/user'
import { CloseTab } from './close-tab'
import { DocumentsTab } from './documents-tab'
import classes from './index.module.scss'
import { StatementsTab } from './statements-tab'
import { TradeConfirmationsTab } from './trade-confirmations-tab'
import { TradesTab } from './trades-tab'
import { TransferTab } from './transfer-tab'

interface Props {
  note: ApiNote
}

interface ConfirmationSummary {
  accountId: string
  accountName: string
  date: string
  timestamp: string
  url: string
}

export const NotesDetail = ({ note }: Props) => {
  const { noteId, noteTab } = useParams()
  const { data: accountData, isLoading: isAccountLoading } = useGetByTenantAccountQuery({
    tenant: TENANT
  })
  const { data: user } = useUser()
  const { userRoles } = getUserDisplayInfo(user)

  const { associatedTrades: trades, businessUnitId } = note
  const startDate = new Date(note.startDate)
  const firstStatementDate = new Date('2022-07-25') // Statements only began generating from 25th July 2022
  const flooredStartDate = startDate < firstStatementDate ? firstStatementDate : startDate
  const maxTaxClassificationChangeDate = new Date(startDate)
  maxTaxClassificationChangeDate.setDate(maxTaxClassificationChangeDate.getDate() + 30)
  const { data: confirmationsSummary, isLoading } =
    useGetByTenantDocumentationNoteAndNoteIdSummaryQuery({
      noteId,
      tenant: TENANT
    })

  if (isLoading || isAccountLoading) {
    return <Loading />
  }

  const pastStatementDates = isThisMonth(flooredStartDate)
    ? []
    : getLastDayOfEveryMonthBetweenDates(flooredStartDate, subMonths(new Date(), 1))

  const activeConfirmationSummaries = (confirmationsSummary ?? []).filter(
    confirmation => confirmation.stage === 'Final'
  )

  const confirmationDetails = activeConfirmationSummaries.map(summary => {
    const [, accountId, date] = summary.fileName.split('_')
    const { accountName } = trades.find(trade => trade.accountId === accountId) || {}
    return {
      url: DOWNLOAD_API.exportTradeConfirmation(accountId, summary.number),
      accountId,
      accountName,
      date,
      timestamp: summary.timestamp
    }
  })

  function sortFunction(a: ConfirmationSummary, b: ConfirmationSummary) {
    const date1String = a.date.split('.')[0]
    const date2String = b.date.split('.')[0]
    const date1 = new Date(
      Number(date1String.split('-')[2]),
      Number(date1String.split('-')[1]),
      Number(date1String.split('-')[0])
    )
    const date2 = new Date(
      Number(date2String.split('-')[2]),
      Number(date2String.split('-')[1]),
      Number(date2String.split('-')[0])
    )
    if (date1 < date2) {
      return 1
    }
    if (date2 < date1) {
      return -1
    }
    if (a.timestamp <= b.timestamp) {
      return -1
    }
    return 1
  }

  confirmationDetails.sort((a, b) => sortFunction(a, b))

  const accountWithinTheNote = accountData.accounts.find(account => account.noteId === noteId)

  const extendedTrades = trades.map(trade => {
    const matchingAccount = accountData.accounts.find(
      account => account.accountId === trade.accountId
    )
    return {
      ...trade,
      accountSource: matchingAccount?.accountSource,
      portfolioType: matchingAccount?.portfolioType,
      strategyTemplateName: matchingAccount?.strategyTemplateName,
      last30DaysPeformancePercent: matchingAccount?.last30DaysPeformancePercent,
      last30DaysPerformance: matchingAccount?.last30DaysPerformance
    }
  })

  const isLocked = accountData.notes.find(innerNote => innerNote.noteId === noteId)?.isLocked
  const isLockedForCollateral = note.isCollateralForLoan && !isCollateralUser(userRoles)
  const isClosed = trades.every(
    trade => trade.accountStatus === 'Closed' || trade.accountStatus === 'Closing'
  )

  const canUserUpdate =
    intersection(user.roles, ['Administrator', 'BankAdmin', 'Editor', 'FLStructuring']).length > 0
  const canUserTransfer =
    intersection(user.roles, ['Administrator', 'BankAdmin', 'Sales']).length > 0

  return (
    <section className={classes.root}>
      <h3>{note.noteName}</h3>
      <div className="mt-8">
        <Tabs.List spacing="md">
          <Tabs.Tab
            className="ml-3"
            isSelected={noteTab === 'details'}
            to={PATH.reportsSingleNote(noteId, 'details')}
            value="details"
          >
            Details
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={noteTab === 'trades'}
            to={PATH.reportsSingleNote(noteId, 'trades')}
            value="trades"
          >
            Trades
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={noteTab === 'statements'}
            to={PATH.reportsSingleNote(noteId, 'statements')}
            value="statements"
          >
            Statements
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={noteTab === 'trade-confirmations'}
            to={PATH.reportsSingleNote(noteId, 'trade-confirmations')}
            value="trade-confirmations"
          >
            Trade confirmations
          </Tabs.Tab>
          <Tabs.Tab
            isSelected={noteTab === 'documents'}
            to={PATH.reportsSingleNote(noteId, 'documents')}
            value="documents"
          >
            Documents
          </Tabs.Tab>
          {canUserUpdate && (
            <Tabs.Tab
              isSelected={noteTab === 'top-up-withdraw'}
              to={PATH.reportsSingleNote(noteId, 'top-up-withdraw')}
              value="top-up-withdraw"
            >
              Top up or withdraw
            </Tabs.Tab>
          )}
          {canUserTransfer && (
            <Tabs.Tab
              isSelected={noteTab === 'transfer'}
              to={PATH.reportsSingleNote(noteId, 'transfer')}
              value="transfer"
            >
              Transfer
            </Tabs.Tab>
          )}
          {canUserUpdate && (
            <Tabs.Tab
              isSelected={noteTab === 'close'}
              to={PATH.reportsSingleNote(noteId, 'close')}
              value="close"
            >
              Close
            </Tabs.Tab>
          )}
        </Tabs.List>
      </div>
      {noteTab === 'details' && (
        <Tabs.Panel className={classes.tabPanel} value="details">
          {accountWithinTheNote ? (
            <NoteDetailsTable
              accountId={accountWithinTheNote.accountId}
              accountSource={accountWithinTheNote.accountSource}
            />
          ) : (
            <NoteDetailsTableStubbed />
          )}
        </Tabs.Panel>
      )}
      {noteTab === 'trades' && (
        <Tabs.Panel className={classes.tabPanel} value="trades">
          <TradesTab businessUnitId={businessUnitId} noteId={noteId} trades={extendedTrades} />
        </Tabs.Panel>
      )}
      {noteTab === 'statements' && (
        <Tabs.Panel className={classes.tabPanel} value="statements">
          <StatementsTab noteId={noteId} pastStatementDates={pastStatementDates} />
        </Tabs.Panel>
      )}
      {noteTab === 'trade-confirmations' && (
        <Tabs.Panel className={classes.tabPanel} value="trade-confirmations">
          <TradeConfirmationsTab confirmationDetails={confirmationDetails} />
        </Tabs.Panel>
      )}
      {noteTab === 'documents' && (
        <Tabs.Panel className={classes.tabPanel} value="documents">
          <DocumentsTab />
        </Tabs.Panel>
      )}
      {noteTab === 'top-up-withdraw' && (
        <Tabs.Panel className={classes.tabPanel} value="top-up-withdraw">
          <TopUpWithdrawNoteForm
            isClosed={isClosed}
            isLocked={isLocked}
            isLockedForCollateral={isLockedForCollateral}
          />
        </Tabs.Panel>
      )}
      {noteTab === 'transfer' && (
        <Tabs.Panel className={classes.tabPanel} value="transfer">
          <TransferTab
            businessUnitName={accountWithinTheNote.businessUnit.businessUnitName}
            counterpartyId={note.counterPartyId}
            isClosed={isClosed}
            isLocked={isLocked}
            noteId={noteId}
          />
        </Tabs.Panel>
      )}
      {noteTab === 'close' && (
        <Tabs.Panel className={classes.tabPanel} value="close">
          <CloseTab
            isClosed={isClosed}
            isLocked={isLocked}
            isLockedForCollateral={isLockedForCollateral}
            trades={extendedTrades}
          />
        </Tabs.Panel>
      )}
    </section>
  )
}
